import { useCallback, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { replaceUnavailableNumbers } from '../utils/numberReplacementUtils';
import type { PlaceOrderFormData } from '../placeOrderTypes';

export interface UseUnavailableNumbersHandlerResult {
  unavailableNumbers: string[];
  showUnavailableNumbersModal: boolean;
  isReplacingNumbers: boolean;
  handleNumbersUnavailable: (numbers: string[]) => void;
  handleReplaceAndSubmit: () => Promise<void>;
  handleCancelNumberReplacement: () => void;
  setSubmitOrder: (fn: (data: PlaceOrderFormData) => Promise<void>) => void;
}

/**
 * Hook to handle unavailable phone numbers during order submission.
 * Manages the modal state and replacement logic.
 */
export const useUnavailableNumbersHandler = (): UseUnavailableNumbersHandlerResult => {
  const methods = useFormContext<PlaceOrderFormData>();
  const [unavailableNumbers, setUnavailableNumbers] = useState<string[]>([]);
  const [showUnavailableNumbersModal, setShowUnavailableNumbersModal] = useState(false);
  const [isReplacingNumbers, setIsReplacingNumbers] = useState(false);
  const submitOrderRef = useRef<((data: PlaceOrderFormData) => Promise<void>) | null>(null);

  const setSubmitOrder = useCallback((fn: (data: PlaceOrderFormData) => Promise<void>) => {
    submitOrderRef.current = fn;
  }, []);

  const handleNumbersUnavailable = useCallback((numbers: string[]) => {
    setUnavailableNumbers(numbers);
    setShowUnavailableNumbersModal(true);
  }, []);

  const handleReplaceAndSubmit = useCallback(async () => {
    if (!submitOrderRef.current) {
      // eslint-disable-next-line no-console
      console.error('submitOrder not set');
      return;
    }

    setIsReplacingNumbers(true);
    try {
      const currentData = methods.getValues();
      const updatedUnits = await replaceUnavailableNumbers(
        currentData.products.placeOrderItemUnits,
        unavailableNumbers
      );

      // Update form with new numbers
      methods.setValue('products.placeOrderItemUnits', updatedUnits);

      // Close modal and submit with updated data
      setShowUnavailableNumbersModal(false);
      setUnavailableNumbers([]);

      // Submit order with updated form data
      const updatedData = methods.getValues();
      await submitOrderRef.current(updatedData);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to replace numbers:', error);
    } finally {
      setIsReplacingNumbers(false);
    }
  }, [methods, unavailableNumbers]);

  const handleCancelNumberReplacement = useCallback(() => {
    setShowUnavailableNumbersModal(false);
    setUnavailableNumbers([]);
  }, []);

  return {
    unavailableNumbers,
    showUnavailableNumbersModal,
    isReplacingNumbers,
    handleNumbersUnavailable,
    handleReplaceAndSubmit,
    handleCancelNumberReplacement,
    setSubmitOrder,
  };
};
